import { HttpError } from '@magicdoorinc/errors';
import { useLocalization, IconCheck, IconSearch, Popover, Button, InfiniteScroll, cn } from '@magicdoorinc/ui';
import { For, Show, createEffect, createMemo, createSignal, equalFn, on } from 'solid-js';
import { stringToColor } from '~/utils/strings';
import { debounce, deepEqual } from '~/utils/tool';
import type { LabeledGroupProps } from '@magicdoorinc/ui';
import type { JSX } from 'solid-js';
import type { Promisable } from '~/utils/types';

export const searchSelectValuePropNames = [
  'multiple',
  'selected',
  'placeholder',
  'onInput',
  'exclude',
  'defaultSelected',
  'filter',
  'defaultSelectedIds',
  'disabledItem',
  'renderItem',
] as const;

export type LabeledSearchSelectProps<T extends { id: unknown }, U extends boolean | undefined> = Omit<EntrySearchProps<T, U>, 'onSelect'> &
  LabeledGroupProps & {
    value?: U extends true ? string[] : string;
    onInput?: (value: U extends true ? string[] : string, selected: U extends true ? T[] : T) => void;
  };

export type EntrySearchProps<
  T extends { id: unknown },
  U extends boolean | undefined,
  F extends Record<string, unknown> = Record<string, unknown>,
> = {
  placeholder?: string;
  multiple?: U;
  selected?: U extends true ? T[] : T;
  onSelect?: (selected: U extends true ? T[] : T) => void;
  exclude?: (item: T) => boolean | undefined;
  disabledItem?: (item: T) => JSX.Element;
  doNotRenderSelected?: boolean;
  filter?: F;
  minimumInputLength?: number;
  defaultSelected?: U extends true ? T[] : T;
  defaultSelectedIds?: U extends true ? string[] : string;
  initialSelectedLoader?: (ids: string[]) => Promise<T[]>;
  class?: string;
  prefix?: string;
  renderItem?: (item: T) => JSX.Element;
  id?: string;
};

export const SearchSelect = <T extends { id: unknown }, U extends boolean | undefined = undefined>(
  props: {
    fetcher: (query: string | undefined, page: number) => Promisable<{ items: T[]; totalPages: number }>;
    renderItem: (
      item: T,
      meta: {
        disabledLabel?: JSX.Element;
      }
    ) => JSX.Element;
    renderSelected: keyof T | ((item: T) => JSX.Element);
  } & Omit<EntrySearchProps<T, U>, 'renderItem'>
) => {
  const { t } = useLocalization();
  const [list, setList] = createSignal<T[]>([]);
  const [pagination, setPagination] = createSignal<{ page: number; totalPages: number }>();
  const [error, setError] = createSignal<unknown>();
  const [query, setQuery] = createSignal('');
  const [selected, setSelected] = createSignal<T[]>([]);
  const [latestDefaultSelectedIds, setLatestDefaultSelectedIds] = createSignal<string[]>();
  const [open, setOpen] = createSignal(false);

  let inputRef!: HTMLInputElement;
  let loading = false;
  let latestOpen = false;
  let latestMinimumInputLength: number | undefined;

  createEffect(
    on(
      () => ({ selected: props.selected, defaultSelected: props.defaultSelected }),
      (current, prev) => {
        let _selected = current.selected;
        if (!_selected && current.defaultSelected && !equalFn(current.defaultSelected, prev?.defaultSelected)) {
          _selected = current.defaultSelected;
        }
        if (_selected) {
          setSelected((Array.isArray(_selected) ? _selected : [_selected]) as T[]);
        }
      }
    )
  );

  const loadInitialSelected = async (defaultSelectedIds: string[]) => {
    if (!props.initialSelectedLoader || !defaultSelectedIds?.length) {
      setSelected([]);
      return;
    }

    const currentIdsSnapshot = defaultSelectedIds;
    const items = await props.initialSelectedLoader(defaultSelectedIds);
    if (!deepEqual(currentIdsSnapshot, latestDefaultSelectedIds())) {
      return;
    }

    setSelected(items);
  };

  createEffect(
    on(
      () => props.defaultSelectedIds,
      (current, prev) => {
        const array = toArray(current);
        setLatestDefaultSelectedIds(array);

        if (deepEqual(array, toArray(prev))) {
          return;
        }
        loadInitialSelected(array);
      }
    )
  );

  createEffect(() => {
    latestOpen = open();
    latestMinimumInputLength = props.minimumInputLength;
  });

  const toArray = <T,>(value?: U extends true ? T[] : T): T[] => {
    return (value ? (Array.isArray(value) ? value : [value]) : []).filter(Boolean);
  };

  const isSelected = (item: T) => selected().some((i) => i.id === item.id);

  const ended = () => {
    const c = pagination();
    return c != null && c.totalPages <= c.page;
  };

  const renderSelected = createMemo(() => {
    if (typeof props.renderSelected === 'function') return props.renderSelected;
    const key = props.renderSelected;
    return (item: T) => item[key] as string;
  });

  const errorMessage = () => {
    const err = error();
    if (err == null) return;
    if (err instanceof HttpError) {
      const msg = Object.values(err.getErrors()).flat();
      return msg.toString();
    }
    if (err instanceof Error) {
      return err.message;
    }
    return t('Operation failed, please try again later');
  };

  const handleQuery = debounce((q: string) => {
    if (
      !q ||
      latestMinimumInputLength === undefined ||
      (q && latestMinimumInputLength !== undefined && q.length >= latestMinimumInputLength)
    ) {
      setQuery(q);
      setPagination(undefined);
      setError(undefined);
      setList([]);
      if (latestOpen) handleLoad();
    }
  }, 500);

  const handleSelect = (e: MouseEvent, item: T) => {
    if ((e.currentTarget as Element).ariaDisabled === 'true') {
      e.preventDefault();
      return;
    }
    if (props.multiple) {
      if (!props.doNotRenderSelected) {
        if (isSelected(item)) {
          setSelected((prev) => prev.filter((i) => i.id !== item.id));
        } else {
          setSelected((prev) => [...prev, item]);
        }
      }
      props.onSelect?.(selected() as U extends true ? T[] : T);
      setQuery('');
      e.preventDefault();
    } else {
      if (!props.doNotRenderSelected) {
        setSelected([item]);
      }
      props.onSelect?.(item as U extends true ? T[] : T);
      setQuery('');
    }
  };

  const handleRemove = (e: MouseEvent, item: T) => {
    e.stopImmediatePropagation();
    setSelected((prev) => prev.filter((i) => i.id !== item.id));
    props.onSelect?.(selected() as U extends true ? T[] : T);
  };

  const handleLoad = async () => {
    if (loading) return;
    loading = true;
    try {
      const page = (pagination()?.page || 0) + 1;
      const { items, totalPages } = await props.fetcher(query(), page);
      setPagination({ page, totalPages });
      const prevLength = list().length;
      setList((prev) =>
        [...prev, ...items].filter((i, index, self) => !props.exclude?.(i) && index === self.findIndex((t) => t.id === i.id))
      );
      if (list().length === prevLength && totalPages > page) {
        loading = false;
        await handleLoad();
      }
    } catch (err) {
      setError(err);
    } finally {
      loading = false;
    }
  };

  const handleOpenOnly = (e: MouseEvent) => {
    const trigger = e.currentTarget as Element;
    inputRef.focus();
    if (trigger.ariaExpanded === 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  };

  return (
    <Popover
      open={open()}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setPagination(undefined);
          setError(undefined);
          setList([]);
          handleLoad();
        } else {
          handleQuery('');
        }
      }}
      onOutsideClick={(e: MouseEvent) => (e.currentTarget as Node).contains(e.relatedTarget as Node) && e.preventDefault()}>
      <Popover.Trigger
        id={`${props.id}`}
        class="relative flex w-full flex-wrap items-center gap-2 rounded-md border border-border bg-white px-2.5 py-0.5 text-sm text-text-02 outline-none transition-all ease-in-out placeholder:text-text-disabled focus-within:border-primary focus-within:bg-input-bg-active aria-expanded:border-primary hover-allowed:hover:border-primary"
        onClick={handleOpenOnly}>
        <Show when={props.prefix}>
          <span class="select-none whitespace-nowrap text-text-03">{props.prefix}: </span>
        </Show>
        <Show when={selected().length > 0}>
          <div class="flex min-w-0 select-none flex-wrap gap-1 py-1">
            <For each={selected()}>
              {(item, index) => {
                const label = renderSelected()(item);
                return (
                  <button
                    id={`${props.id}-${index()}-remove-btn`}
                    type="button"
                    class="flex min-w-0 max-w-full items-center gap-1 truncate rounded-md bg-current-alpha px-1.5 text-sm text-[--c] after:text-base after:content-['×']"
                    style={{ '--c': stringToColor(typeof label === 'string' ? label : (item.id as string)) }}
                    onClick={(e) => handleRemove(e, item)}
                    title={t('Remove select')}>
                    {renderSelected()(item)}
                  </button>
                );
              }}
            </For>
          </div>
        </Show>
        <input
          id={`${props.id}-input`}
          autocomplete="off"
          ref={inputRef}
          class={cn(
            'min-w-0 flex-1 truncate bg-transparent py-1.5 outline-none placeholder:text-text-disabled',
            !props.multiple && selected().length > 0 ? 'w-0 flex-none p-0' : 'basis-20 px-1'
          )}
          value={query()}
          onInput={(e) => handleQuery(e.currentTarget.value)}
          onKeyUp={(e) => {
            if (query() || e.key !== 'Backspace' || selected()?.length === 0) return;
            props.onSelect?.(setSelected((prev) => prev.slice(0, -1)) as U extends true ? T[] : T);
          }}
          placeholder={!props.multiple && selected().length !== 0 ? undefined : (props.placeholder ?? t('Search'))}
        />
        <IconSearch class="pointer-events-none absolute bottom-2.5 right-3 size-4 text-text-03" />
      </Popover.Trigger>
      <Popover.Content
        aria-disable-swipe
        as="div"
        class="my-1 flex max-h-dropdown min-w-[--reference-width] flex-col overflow-hidden rounded-md border border-separator bg-white p-1 text-sm text-text-02 shadow-lg">
        <div class="thinscroll flex-1 space-y-0.5 overflow-auto">
          <InfiniteScroll threshold={100} ended={error() != null || ended()} endMessage={errorMessage()} onReachEnd={handleLoad}>
            <For each={list()}>
              {(item, index) => {
                const disabledLabel = props.disabledItem?.(item);
                return (
                  <Popover.Trigger
                    id={`${props.id}-item-${index()}`}
                    as="li"
                    class="flex cursor-pointer items-center justify-between rounded-md px-3 py-2 transition-colors aria-checked:bg-light-pink aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover-allowed:hover:bg-light-pink"
                    onClick={(e) => {
                      if (props.multiple) {
                        e.preventDefault();
                        e.stopImmediatePropagation?.();
                      }
                      handleSelect(e, item);
                    }}
                    aria-checked={isSelected(item)}
                    aria-disabled={!!disabledLabel}>
                    {props.renderItem(item, { disabledLabel })}
                    <Show when={isSelected(item)}>
                      <IconCheck class="size-5 text-primary" />
                    </Show>
                  </Popover.Trigger>
                );
              }}
            </For>
          </InfiniteScroll>
        </div>
        <Show when={props.multiple}>
          <div class="sticky bottom-0 flex justify-end border-t border-border bg-white pt-2">
            <Button id={`${props.id}-done-button`} size="sm" variant="outlined" onClick={() => setOpen(false)}>
              {t('Done')}
            </Button>
          </div>
        </Show>
      </Popover.Content>
    </Popover>
  );
};
